import React, { useCallback, useEffect, useRef, useState } from 'react';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import SegmentedTabs from 'shared/components/ui/SegmentedTabs';
import DrawerTab from 'shared/components/ui/DrawerTab';
import { X, Trash2, Play, Loader2 } from 'lucide-react';
import { useTheme } from 'shared/theme/ThemeContext';
import ModelParamsPopover from 'shared/components/ui/ModelParamsPopover';
import VariableEditor, { insertAtCursor } from './VariableEditor';
import SchemaRail from './SchemaRail';
import { useTraceTags, tagGroup, hiddenByTags, withVars, setVars } from './SpanVariables';
import FilterRulesEditor from './FilterRulesEditor';
import OutputFieldsEditor from './OutputFieldsEditor';
import ContextFieldsEditor from './ContextFieldsEditor';
import EdgesEditor from './EdgesEditor';
import OutcomesEditor from './OutcomesEditor';
import SemanticEditor from './SemanticEditor';
import DecisionEditor from './DecisionEditor';
import DecisionRoutes from './DecisionRouteEditor';
import TagInput from './TagInput';
import * as outcomes from './outcomes';
import { ProviderDropdown, ModelDropdown } from './modelParts';
import { NodeMark } from './EvalNode';
import { kindOf, kindMeta, decisionFromSemantic } from './nodeKinds';
import { numericUpstreamFields, displayName, stepsGroup } from './graphModel';
import { Step as StepRow } from '../components/ResultPath';
import { Section, Field, codeFrame } from './formParts';

/**
 * A node's settings, in a panel beside the canvas — the canvas shrinks to
 * make room, the step being edited stays visible in the graph.
 *
 * Inside, each kind gets the SAME editor the current evals have for that
 * kind: Python → a code editor with the trace fields beside it; LLM judge →
 * provider, model, params and a prompt editor with {{field}} pills; Filter →
 * rules; Human → instructions and the options a grader picks from; Verdict →
 * pass/fail, reason, which score to show.
 *
 * Tabs: Settings · Output · Test — the drawers' own tab strip
 * (DrawerTab), since they are sections of one panel, not modes of a setting.
 */

const MIN_W = 380;
const MAX_W = 820;
const DEFAULT_W = 520;
const WIDTH_KEY = 'trodo.eval3NodePanel.width';

function readWidth() {
  try {
    const raw = Number(localStorage.getItem(WIDTH_KEY));
    if (Number.isFinite(raw) && raw >= MIN_W && raw <= MAX_W) return raw;
  } catch (_) { /* private mode */ }
  return DEFAULT_W;
}

/** A secondary action in the panel: Run, Run only this step. */
const actionBtn = 'inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] px-2.5 text-[12px] font-medium text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover disabled:opacity-50';
const iconBtn = 'inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors';

// ---------------------------------------------------------------------------
// Per-kind editors
// ---------------------------------------------------------------------------

function PythonEditor({ node, update, schema, theme, readOnly, graph, nodeId, scopes, target, teamId, agentName }) {
  const editorRef = useRef(null);
  const before = graph && nodeId ? stepsGroup(graph, nodeId) : [];
  const cfg = node.config || {};
  const tgt = target || schema?.target || 'run';
  // Spans (and a conversation's runs) dragged in are tags: t.vars["NAME"], the text a judge would get.
  const tags = useTraceTags({
    vars: cfg.span_vars, target: tgt, mode: 'python', scopes, teamId, agentName,
    getTexts: () => [cfg.source || ''],
    apply: ([source], vars) => update({ config: setVars({ ...cfg, source }, vars) }),
  });
  return (
    <>
      <Section title="Python code" hint="def evaluate(t) — return the outcome (True/False, a number, or one of the options). To pass something on, return it second: return ok, {'excerpts': [...]}. No network.">
        <div className={codeFrame}>
          <VariableEditor height="360px" language="python" mode="python" theme={theme} editorRef={editorRef} readOnly={readOnly}
            onEditVar={readOnly ? null : tags.edit} describeVar={tags.describeVar}
            value={cfg.source || ''} onChange={(v) => update({ config: withVars({ ...cfg, source: v }, [v], 'python') })} />
        </div>
      </Section>
      {/* What the step passes on, apart from what it decides. */}
      <Section title="Context" hint="what this step passes on to later steps. A judge reads it as {{steps.<this step>.<name>}}, Python as t.steps.<this step>.<name>. Return it as the second value: return ok, {'excerpts': [...]}">
        <ContextFieldsEditor value={node.context} readOnly={readOnly} taken={Object.keys(node.output || {})}
          onChange={(context) => update({ context })} />
      </Section>
      <Section title="Fields" hint="drag into the code, or click to insert at the caret. text(x) turns any field into a string; a Spans tag reads as t.vars[name], and its pencil sets which spans.">
        <SchemaRail schema={schema} extraGroups={[tagGroup(tags.units, tags.tagRef), ...before].filter(Boolean)} hidePaths={hiddenByTags(tgt)}
          refOf={(f) => f.python} onPick={(f) => insertAtCursor(editorRef.current, f.python)} />
      </Section>
      {tags.dialog}
    </>
  );
}

function JudgeEditor({ node, update, schema, theme, readOnly, integrations, modelsFor, graph, nodeId, scopes, target, teamId, agentName }) {
  const editorRef = useRef(null);
  const before = graph && nodeId ? stepsGroup(graph, nodeId) : [];
  const cfg = node.config || {};
  const integrationId = cfg.integration_id ?? '';
  const selected = (integrations || []).find((i) => String(i.id) === String(integrationId));
  const prompt = typeof cfg.prompt === 'string' ? { user: cfg.prompt } : (cfg.prompt || {});
  const tgt = target || schema?.target || 'run';
  // A tag's text is in either prompt; both are kept in step with span_vars.
  const withPrompt = (next) => ({ ...cfg, prompt: { ...prompt, ...next } });
  const tags = useTraceTags({
    vars: cfg.span_vars, target: tgt, mode: 'prompt', scopes, teamId, agentName,
    getTexts: () => [prompt.system || '', prompt.user || ''],
    apply: ([system, user], vars) => update({ config: setVars(withPrompt({ system: system || undefined, user }), vars) }),
  });
  return (
    <>
      {/* Provider, model and the generation params on one row: three choices about one thing. */}
      <Section title="Model" hint={cfg.integration_id == null ? 'this judge needs a provider and a model' : undefined}>
        <div className="grid grid-cols-1 items-end gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
          <Field label="Provider">
            <ProviderDropdown integrations={integrations} value={integrationId} disabled={readOnly}
              onChange={(id) => {
                const next = integrations.find((i) => String(i.id) === String(id));
                const models = modelsFor(next);
                // Keep the model if the new provider has it; else its default; else its only model.
                const model = models.includes(cfg.model) ? cfg.model
                  : next?.default_model && models.includes(next.default_model) ? next.default_model
                    : models.length === 1 ? models[0] : undefined;
                update({ config: { ...cfg, integration_id: Number(id), model } });
              }} />
          </Field>
          <Field label="Model">
            <ModelDropdown integration={selected} models={modelsFor(selected)} value={cfg.model || ''} disabled={readOnly}
              onChange={(m) => update({ config: { ...cfg, integration_id: cfg.integration_id ?? selected?.id, model: m } })} />
          </Field>
          <div className="pb-px">
            <ModelParamsPopover value={cfg.model_params || {}} disabled={readOnly} onChange={(p) => update({ config: { ...cfg, model_params: p } })} />
          </div>
        </div>
        {(integrations || []).length === 0 && <div className="mt-1 text-[11px] text-warning">No providers yet. Add one in Settings → Models.</div>}
      </Section>
      <Section title="Judge prompt" hint="the model answers with the declared output fields as JSON; that instruction is appended for you">
        <div className={codeFrame}>
          <VariableEditor height="300px" language="markdown" mode="prompt" theme={theme} editorRef={editorRef} readOnly={readOnly}
            onEditVar={readOnly ? null : tags.edit} describeVar={tags.describeVar}
            value={prompt.user || ''} onChange={(v) => update({ config: withVars(withPrompt({ user: v }), [prompt.system, v], 'prompt') })} />
        </div>
        <details className="mt-1.5">
          <summary className="cursor-pointer text-[12px] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]">System prompt</summary>
          <textarea value={prompt.system || ''} readOnly={readOnly} rows={3} aria-label="System prompt"
            onChange={(e) => update({ config: withVars(withPrompt({ system: e.target.value || undefined }), [e.target.value, prompt.user], 'prompt') })}
            className="input mt-1 min-h-[70px] w-full" />
        </details>
      </Section>
      <Section title="Fields" hint="drag into the prompt, or click to insert">
        <SchemaRail schema={schema} extraGroups={[tagGroup(tags.units, tags.tagRef), ...before].filter(Boolean)} hideTypes={['list']} hidePaths={hiddenByTags(tgt)}
          refOf={(f) => f.prompt} onPick={(f) => insertAtCursor(editorRef.current, f.prompt)} maxHeight={200} />
      </Section>
      {tags.dialog}
    </>
  );
}

function FilterEditor({ node, update, schema, scopes, target, readOnly, teamId }) {
  // The eval's target is the root level; `scopes` carries every level a
  // block can open (a run's spans, a conversation's runs), same as Settings.
  const fields = (schema?.groups || []).flatMap((g) => g.fields).filter((f) => f.type !== 'list');
  return (
    <Section title="Filters" hint="evaluated in-process on every event, no sandbox and no model">
      <FilterRulesEditor value={node.config?.filter} target={target || schema?.target || 'run'} scopes={scopes} fields={fields} readOnly={readOnly} teamId={teamId}
        onChange={(filter) => update({ config: { ...node.config, filter } })} />
    </Section>
  );
}

/**
 * A human step asks a person for exactly one thing. There are three shapes
 * that thing takes, and the shape decides the control the grading queue
 * gives the reviewer: buttons, a pair of thumbs, or a scale.
 *
 * Changing the shape changes the field the routes below read, so the routes
 * cannot survive it — they are reset to the one they already fell through to,
 * which keeps the graph connected while the rules are rebuilt.
 */
const ANSWER_SHAPES = [
  { value: 'enum', label: 'Pick one', hint: 'a list of options; each one is a route below' },
  { value: 'boolean', label: 'Yes or no', hint: 'two options, shown as thumbs in the queue' },
  { value: 'number', label: 'A rating', hint: 'a number in a range; the routes threshold it' },
];
const SHAPE_DEFAULTS = {
  enum: { field: 'choice', spec: { type: 'enum', values: ['yes', 'no'] } },
  boolean: { field: 'acceptable', spec: { type: 'boolean' } },
  number: { field: 'rating', spec: { type: 'number', min: 1, max: 5, step: 1 } },
};

function HumanEditor({ node, update, readOnly }) {
  const qc = node.config?.queue_config || {};
  const [field, spec] = Object.entries(node.output || {})[0] || ['choice', { type: 'enum', values: [] }];
  const shape = ANSWER_SHAPES.some((s) => s.value === spec.type) ? spec.type : 'enum';
  const setConfig = (next) => update({ config: { ...node.config, queue_config: { ...qc, ...next } } });
  const setSpec = (next) => update({ output: { [field]: { ...spec, ...next } } });

  const setShape = (value) => {
    if (value === shape) return;
    const d = SHAPE_DEFAULTS[value];
    const fallback = (node.edges || []).find((e) => e.default)?.default || (node.edges || [])[0]?.to;
    update({
      output: { [d.field]: d.spec },
      ...(fallback ? { edges: [{ default: fallback }] } : {}),
    });
  };

  return (
    <>
      <Section title="Instructions for graders" hint="shown beside the trace in the grading queue">
        <textarea value={qc.instructions || ''} readOnly={readOnly} rows={5} aria-label="Instructions for graders"
          onChange={(e) => setConfig({ instructions: e.target.value })} className="input min-h-[120px] w-full" />
      </Section>

      <Section title="What the grader answers" hint="the shape of the answer decides the control they get, and the routes below read it">
        <SegmentedTabs ariaLabel="What the grader answers" value={shape} options={ANSWER_SHAPES}
          onChange={(v) => { if (!readOnly) setShape(v); }} />
      </Section>

      {shape === 'enum' && (
        <Section title="Options" hint="what the grader picks from; each option is a route below">
          <TagInput value={spec.values || []} readOnly={readOnly} ariaLabel="Grader options" placeholder="Type an option and press Enter" min={1}
            onChange={(values) => setSpec({ values })} />
        </Section>
      )}

      {shape === 'number' && (
        <>
          <Section title="The scale" hint="a grader can only land on values the step divides into. Leave Moves in empty for a scale a grader drags rather than presses.">
            <div className="grid grid-cols-3 gap-2">
              {[['min', 'Lowest'], ['max', 'Highest'], ['step', 'Moves in']].map(([key, label]) => (
                <Field key={key} label={label}>
                  <input type="number" value={spec[key] ?? ''} readOnly={readOnly} aria-label={label} className="input w-full"
                    placeholder={key === 'step' ? 'any' : undefined}
                    onChange={(e) => {
                      const n = e.target.value === '' ? undefined : Number(e.target.value);
                      setSpec({ [key]: Number.isFinite(n) ? n : undefined });
                    }} />
                </Field>
              ))}
            </div>
          </Section>
          <Section title="What the ends mean" hint="optional; shown beside the scale so a 1 is not a bare 1">
            <div className="grid grid-cols-2 gap-2">
              <Field label="Lowest">
                <input value={qc.labels?.min || ''} readOnly={readOnly} aria-label="What the lowest means" placeholder="unusable" className="input w-full"
                  onChange={(e) => setConfig({ labels: { ...(qc.labels || {}), min: e.target.value || undefined } })} />
              </Field>
              <Field label="Highest">
                <input value={qc.labels?.max || ''} readOnly={readOnly} aria-label="What the highest means" placeholder="perfect" className="input w-full"
                  onChange={(e) => setConfig({ labels: { ...(qc.labels || {}), max: e.target.value || undefined } })} />
              </Field>
            </div>
          </Section>
        </>
      )}
    </>
  );
}

function VerdictEditor({ graph, nodeId, node, update, readOnly }) {
  const cfg = node.config || {};
  const scores = numericUpstreamFields(graph, nodeId);
  const scoreKey = cfg.score_from ? `${cfg.score_from.node_id}.${cfg.score_from.field}` : '';
  return (
    <>
      <Section title="Verdict">
        <SegmentedTabs ariaLabel="Verdict" value={cfg.passed === false ? 'fail' : 'pass'}
          options={[{ value: 'pass', label: 'Pass' }, { value: 'fail', label: 'Fail' }]}
          onChange={(v) => {
            if (readOnly) return;
            const passed = v === 'pass';
            update({ config: { ...cfg, passed, reason_code: passed ? undefined : cfg.reason_code } });
          }} />
      </Section>
      {cfg.passed === false && (
        <Section title="Reason code" hint="a short tag results are grouped by">
          <input value={cfg.reason_code || ''} readOnly={readOnly} aria-label="Reason code" placeholder="LOW_SCORE"
            onChange={(e) => update({ config: { ...cfg, reason_code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_') || undefined } })} className="input w-full font-mono" />
        </Section>
      )}
      <Section title="Display score" hint="optional: a number from a node on every path here, shown beside the verdict">
        <BrandDropdown ariaLabel="Display score" value={scoreKey} disabled={readOnly}
          onChange={(v) => {
            const next = { ...cfg };
            if (!v) delete next.score_from; else { const [node_id, f] = v.split('.'); next.score_from = { node_id, field: f }; }
            update({ config: next });
          }}
          options={[{ value: '', label: 'No score, pass/fail only' }, ...scores.map((s) => ({ value: `${s.node_id}.${s.field}`, label: `${displayName(graph, s.node_id)} · ${s.field} (${s.min}–${s.max})` }))]} />
      </Section>
    </>
  );
}

/**
 * The editor for one kind of step, the same one wherever a step is edited:
 * this panel beside the canvas, and the simple eval's form.
 */
export function KindEditor({ kind, node, update, graph, nodeId, readOnly, schema, scopes, target, integrations, modelsFor, teamId, agentName }) {
  const { theme } = useTheme();
  if (kind === 'python') return <PythonEditor node={node} update={update} schema={schema} theme={theme} readOnly={readOnly} graph={graph} nodeId={nodeId} scopes={scopes} target={target} teamId={teamId} agentName={agentName} />;
  if (kind === 'llm_judge') return <JudgeEditor node={node} update={update} schema={schema} theme={theme} readOnly={readOnly} integrations={integrations} modelsFor={modelsFor} graph={graph} nodeId={nodeId} scopes={scopes} target={target} teamId={teamId} agentName={agentName} />;
  if (kind === 'decision_model') return <DecisionEditor node={node} update={update} schema={schema} theme={theme} readOnly={readOnly} graph={graph} nodeId={nodeId} target={target || schema?.target || 'run'} teamId={teamId} scopes={scopes} agentName={agentName} />;
  if (kind === 'ui_filter') return <FilterEditor teamId={teamId} node={node} update={update} schema={schema} scopes={scopes} target={target} readOnly={readOnly} />;
  if (kind === 'human') return <HumanEditor node={node} update={update} readOnly={readOnly} />;
  if (kind === 'verdict') return <VerdictEditor graph={graph} nodeId={nodeId} node={node} update={update} readOnly={readOnly} />;
  if (kind === 'semantic') return <SemanticEditor node={node} update={update} target={target || schema?.target || 'run'} graph={graph} nodeId={nodeId} readOnly={readOnly} teamId={teamId} agentName={agentName} />;
  return <div className="text-[12px] text-[color:var(--text-muted)]">This node uses the “{kind}” instrument, which has no editor yet. Its configuration is kept as saved.</div>;
}

// ---------------------------------------------------------------------------
// The panel
// ---------------------------------------------------------------------------

export default function NodeConfigPanel({
  graph, nodeId, onChange, onDelete, onClose, onNewVerdict, onNewStep,
  schema, scopes = null, target = null, integrations = [], modelsFor = () => [], teamId = null, agentName = null,
  problem = null, step = null, testing = false, onRunTest = null, canTest = true, readOnly = false,
}) {
  const node = graph?.nodes?.[nodeId];
  const kind = kindOf(node);
  const meta = kindMeta(kind);
  const [tab, setTab] = useState('config');
  const [width, setWidth] = useState(readWidth);
  const widthRef = useRef(width);
  widthRef.current = width;

  useEffect(() => { setTab('config'); }, [nodeId]);
  useEffect(() => {
    // Not an Escape a picker handled, nor one meant for a popup open above the panel.
    const onKey = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented || document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const startResize = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = widthRef.current;
    const onMove = (ev) => setWidth(Math.min(MAX_W, Math.max(MIN_W, startW + (startX - ev.clientX))));
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      try { localStorage.setItem(WIDTH_KEY, String(widthRef.current)); } catch (_) { /* private mode */ }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  if (!node) return null;
  const update = readOnly ? () => {} : (patch) => onChange(nodeId, patch);
  const verdict = kind === 'verdict';
  // A step with one output routes by OUTCOME; anything else keeps the expression editor.
  const shape = verdict ? null : outcomes.read(node);


  return (
    <aside className="relative flex h-full shrink-0 flex-col border-l border-[color:var(--border-default)] bg-[color:var(--surface-base)]" style={{ width, fontFamily: 'Inter, sans-serif' }} aria-label={`${meta.label} settings`}>
      <div onMouseDown={startResize} title="Drag to resize" className="absolute left-0 top-0 bottom-0 z-10 -ml-0.5 w-1 cursor-col-resize hover:bg-[color:var(--trodo-primary)]/30" />

      <div className="shrink-0 border-b border-[color:var(--border-default)] px-3 py-3">
        <div className="flex items-center gap-2.5">
          <NodeMark kind={kind} node={node} size={30} />
          <input type="text" value={node.name || ''} readOnly={readOnly} aria-label="Step name" placeholder={meta.label} title={`${meta.label} · ${nodeId}`}
            onChange={(e) => update({ name: e.target.value })}
            className="-ml-1.5 min-w-0 flex-1 rounded-md bg-transparent px-1.5 py-0.5 text-[13.5px] font-semibold text-[color:var(--text-primary)] focus:bg-[color:var(--surface-sunken)] focus:outline-none" />
          <div className="flex shrink-0 items-center gap-0.5">
            {onRunTest && !verdict && (
              <button type="button" onClick={() => onRunTest(nodeId, 'until')} disabled={testing} aria-label="Run up to this step"
                title={canTest ? 'Run the eval up to this step on the test trace' : 'Pick a trace to test on, then run up to this step'}
                className={`${iconBtn} text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)] disabled:opacity-50`}>
                {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <Play className="h-3.5 w-3.5" aria-hidden />}
              </button>
            )}
            {!readOnly && (
              <button type="button" onClick={() => onDelete(nodeId)} title="Delete step" aria-label="Delete step"
                className={`${iconBtn} text-[color:var(--text-muted)] hover:bg-danger-soft hover:text-[color:var(--feedback-error)]`}><Trash2 className="h-3.5 w-3.5" /></button>
            )}
            <button type="button" onClick={onClose} title="Close" aria-label="Close"
              className={`${iconBtn} text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]`}><X className="h-4 w-4" /></button>
          </div>
        </div>
        {problem && <div className="mt-2 text-[11px] text-[color:var(--feedback-error)]">{problem}</div>}
      </div>

      {/* What the step's ▶ returned on the test trace, above its settings. */}
      {!verdict && (step || testing) && (
        <div className="max-h-[40%] shrink-0 overflow-y-auto border-b border-[color:var(--border-default)] px-3 py-2.5" data-testid="step-result">
          {step ? <ol className="text-[12px]" aria-label="This step's result"><StepRow step={step} graph={graph} last /></ol>
            : <div className="text-[12px] text-[color:var(--text-muted)]">Running…</div>}
        </div>
      )}

      <div className="flex shrink-0 items-center gap-1 border-b border-[color:var(--border-default)] px-3">
        <div role="tablist" aria-label="Step panel" className="flex min-w-0 flex-1 items-center gap-1">
          {[{ id: 'config', label: 'Settings' }, ...(verdict ? [] : [{ id: 'routing', label: 'Output' }])].map((t) => (
            <DrawerTab key={t.id} id={t.id} label={t.label} active={tab === t.id} onClick={setTab} role="tab" />
          ))}
        </div>
      </div>

      {tab === 'config' && (
        <fieldset disabled={readOnly} className="min-h-0 min-w-0 flex-1 overflow-y-auto px-3 py-3.5" style={{ border: 0, margin: 0 }}>
          {kind === 'semantic' && !readOnly && (
            <div className="mb-3 flex items-center gap-2 rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] px-2.5 py-2 text-[12px] text-[color:var(--text-secondary)]">
              <span className="min-w-0 flex-1">Semantic checks can no longer be added. This one keeps running as saved.</span>
              <button type="button" onClick={() => onChange(nodeId, decisionFromSemantic(node, target || schema?.target || 'run'))} className={actionBtn}>
                Replace with a decision model
              </button>
            </div>
          )}
          <KindEditor kind={kind} node={node} update={update} graph={graph} nodeId={nodeId} readOnly={readOnly}
            schema={schema} scopes={scopes} target={target} integrations={integrations} modelsFor={modelsFor} teamId={teamId} agentName={agentName} />
        </fieldset>
      )}

      {tab === 'routing' && !verdict && (
        <fieldset disabled={readOnly} className="min-h-0 min-w-0 flex-1 overflow-y-auto px-3 py-3.5" style={{ border: 0, margin: 0 }}>
          {kind === 'decision_model' ? (
            <Section title="Routes">
              <DecisionRoutes graph={graph} nodeId={nodeId} readOnly={readOnly} onChange={(edges) => update({ edges })}
                onNewVerdict={(i, passed) => onNewVerdict(nodeId, i, passed)} onNewStep={(i) => onNewStep(nodeId, i)} />
            </Section>
          ) : shape ? (
            <Section title="This step returns">
              <OutcomesEditor graph={graph} nodeId={nodeId} shape={shape} readOnly={readOnly}
                lockShape={['human', 'ui_filter', 'semantic', 'decision_model'].includes(kind)}
                onChange={(patch) => update(patch)}
                onNewVerdict={(i, passed) => onNewVerdict(nodeId, i, passed)} onNewStep={(i) => onNewStep(nodeId, i)} />
            </Section>
          ) : (
            /* Two outputs, or a condition something else wrote: the expression
               editor, rather than flattening a graph we cannot read back. */
            <>
              <Section title="Output" hint="what this step returns; the routes below branch on it">
                <OutputFieldsEditor value={node.output} readOnly={readOnly} onChange={(output) => update({ output })} />
              </Section>
              <Section title="Then go to" hint="first matching condition wins; every step needs an otherwise">
                <EdgesEditor graph={graph} nodeId={nodeId} readOnly={readOnly} onChange={(edges) => update({ edges })}
                  onNewVerdict={(edgeIndex, passed) => onNewVerdict(nodeId, edgeIndex, passed)} onNewStep={(edgeIndex) => onNewStep(nodeId, edgeIndex)} />
              </Section>
            </>
          )}
        </fieldset>
      )}

    </aside>
  );
}
